import AsyncStorage from '@react-native-async-storage/async-storage';
import apiClient from './api-client';
import i18n from 'i18next';

/**
 * Update price for a gas station
 * @param {Object} priceData - The price data to update
 * @param {string} priceData.placeId - The place ID of the gas station
 * @param {number} priceData.latestPrice - The latest price
 * @param {string} priceData.userId - The user ID
 * @param {string} priceData.recieptUrl - The receipt URL (optional)
 * @returns {Promise<Object>} - The API response
 */
export const updatePrice = async priceData => {
  try {
    const response = await apiClient.post('user/update-price', priceData);

    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error('Error updating price:', error);
    return {
      success: false,
      error: error.response?.data || error.message,
    };
  }
};

/**
 * Get station details by place ID
 * @param {string} placeId - The place ID of the gas station
 * @returns {Promise<Object>} - The API response
 */
export const getStationDetails = async placeId => {
  try {
    const response = await apiClient.post('maps/gas-stations-details', {
      placeId: placeId,
    });

    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error('Error fetching station details:', error);
    return {
      success: false,
      error: error.response?.data || error.message,
    };
  }
};

export const signup = async signupData => {
  try {
    const response = await apiClient.post('auth/signup', signupData);
    console.log('signup response', response?.data?.data);
    return response?.data?.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Signup failed');
  }
};

// Email codes (OTP), as in the backend's Bruno collection ("Email
// verification (signup)" and "Forgot password"). No login token needed.
// All of them answer {Success, Message, Data}; Message is in the x-lang
// language.
//   auth/send-email-otp    {email, phone_number?}  sign-up: send (or resend)
//                          a code. 409 email/phone already registered,
//                          429 resent within 60 s or too many codes.
//   auth/verify-email-otp  {email, otp}  -> Data.verificationToken
//                          400 wrong/expired code, 429 after 5 wrong codes.
//   auth/signup            unchanged; called after the code checks out.
//   auth/forgot-password   {email}  same answer whether or not the email
//                          has an account.
//   auth/verify-reset-otp  {email, otp}  -> Data.resetToken (one use, 15 min)
//   auth/update-password   {resetToken, newPassword, confirmPassword}
//                          6 to 72 characters, must differ from the old one.

// POST to an OTP endpoint. Returns Data; throws with the server's message.
const postAuth = async (path, body) => {
  try {
    const response = await apiClient.post(path, body, {
      headers: {'x-lang': i18n.language === 'dt' ? 'du' : 'en'},
    });
    const data = response?.data;
    console.log(`[${path}]`, response?.status, data?.Message || data?.message);
    // Some endpoints answer HTTP 200 but put a failure flag in the body.
    if (
      data?.Success === 0 ||
      data?.status === false ||
      data?.success === false
    ) {
      throw Object.assign(
        new Error(data?.Message || data?.message || 'Request failed'),
        {handled: true},
      );
    }
    return data?.Data ?? data?.data ?? null;
  } catch (error) {
    if (error.handled) {
      throw error;
    }
    const data = error.response?.data;
    console.log(`[${path}] error`, error.response?.status, data?.Message || data?.message);
    const err = new Error(
      data?.Message ||
        data?.message ||
        (error.response
          ? `Request failed (${error.response.status})`
          : error.message) ||
        'Request failed',
    );
    err.status = error.response?.status;
    throw err;
  }
};

/**
 * Send a 6-digit code to the email being registered (also the Resend button).
 * With phone_number the server also checks the number isn't taken yet.
 */
export const sendSignupOtp = (email, phoneNumber) =>
  postAuth('auth/send-email-otp', {
    email,
    ...(phoneNumber ? {phone_number: phoneNumber} : {}),
  });

/** Check the sign-up code. Resolves with Data ({verificationToken}). */
export const verifySignupOtp = (email, otp) =>
  postAuth('auth/verify-email-otp', {email, otp});

/** Email a password-reset code. */
export const requestPasswordReset = email =>
  postAuth('auth/forgot-password', {email});

/** Check the password-reset code. Resolves with Data ({resetToken}). */
export const verifyResetOtp = (email, otp) =>
  postAuth('auth/verify-reset-otp', {email, otp});

/** Set the new password with the resetToken from verifyResetOtp. */
export const updatePassword = ({resetToken, password}) =>
  postAuth('auth/update-password', {
    resetToken,
    newPassword: password,
    confirmPassword: password,
  });

export const answerQuestionsService = async answerData => {
  try {
    const userData = await AsyncStorage.getItem('Data');
    const parseUserData = JSON.parse(userData);
    const userId = parseUserData?.userId;

    // Flatten answers into top-level key/value pairs: { answer_1: '...', answer_2: '...', ... }
    const answersObject = answerData.reduce((acc, answer, idx) => {
      acc[`answer_${idx + 1}`] = answer;
      return acc;
    }, {});

    const body = {
      userid: userId,
      ...answersObject,
    };

    console.log('answerbody', body);

    const response = await apiClient.post('user/answer-questions', body);
    return response.data.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to save answers');
  }
};

export const getQuestions = async () => {
  try {
    const response = await apiClient.get('user/questions-list');
    return response.data.questions;
  } catch (error) {
    throw new Error('Failed to get questions');
  }
};

/**
 * Get place details for a given lat/lng
 * @param {{lat: number, lng: number}} payload
 * @returns {Promise<{success: boolean, data?: any, error?: any}>}
 */
export const getPlaceDetailsByLatLng = async ({lat, lng}) => {
  try {
    const response = await apiClient.post('user/place-details', {lat, lng});
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error('Error fetching place details:', error);
    return {
      success: false,
      error: error.response?.data || error.message,
    };
  }
};

export const getEstimateSavings = async () => {
  try {
    const userData = await AsyncStorage.getItem('Data');
    const parseUserData = JSON.parse(userData);
    const userId = parseUserData?.userId;
    const response = await apiClient.post('user/after-questions', {userId});
    return response.data.Data;
  } catch (error) {
    throw new Error(
      error.response?.data?.Message || 'Failed to get estimate savings',
    );
  }
};

export const saveStation = async station => {
  try {
    console.log('saving station', station.id);
    const userData = await AsyncStorage.getItem('Data');
    const parseUserData = JSON.parse(userData);
    const userId = parseUserData?.userId;
    const response = await apiClient.post('user/bookmark-gas-station',{
      userId,
      placeId: station.id,
    });
    console.log('save station response', response.data);
    return response.data;
  }catch(err){
    throw new Error(err.response?.data?.Message);
  }
};

export const removeStation = async station => {
  try {
    console.log('removing station', station.id);
    const userData = await AsyncStorage.getItem('Data');
    const parseUserData = JSON.parse(userData);
    const userId = parseUserData?.userId;
    console.log('userId', userId);
    const response = await apiClient.post('user/remove-bookmark',{
      userId,
      placeId: station.placeId || station.id,
    });
    console.log('remove station response', response.data);
    return response.data;
  }catch(err){
    throw new Error(err.response?.data?.Message);
  }
};

export const getFavouritesApi = async ()=>{
  try{
    const userData = await AsyncStorage.getItem('Data');
    const parseUserData = JSON.parse(userData);
    const userId = parseUserData?.userId;
    const response = await apiClient.post('user/bookmarked-gas-stations',{
      userId,
    });
    return response.data;
  }catch(err){
    console.log('get favourites error', err);
    throw new Error(err.response?.data?.Message);
  }
};